import React, { ReactElement, useState } from 'react'
import logo from '../../assets/images/logo.svg'
import { Formik, Form, Field, FormikProps, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, useStripe } from '@stripe/react-stripe-js'
import axios from 'axios'

// Assuming these imports are correct
import { Button, Input, Text } from '../atoms'
import { ModalStripe } from '../molecules'
import PerceptionBarModal from '../molecules/PerceptionBarModal/PerceptionBarModal'
import { toast } from 'react-toastify'

import { logger } from '../../utils'
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51R3zfcIh7t8lXfzQF20z2i7qsHCSrpY2IOFHTbRu9Et39HBVwqg3lcFZxBhVUnZNWK24txreP62lXXbNgzlYSvGz00lnPVMxEl'
const PRODUCT_PRICE_ID = 'price_1RH9NKIh7t8lXfzQHNJoQaov'
const PRODUCT_NAME = 'DopaGE Prevention Portal - 1 year'
const PRODUCT_PRICE_CENTS = 2500
if (!STRIPE_PUBLISHABLE_KEY?.startsWith('pk_')) { console.warn('Stripe Publishable Key seems invalid or is not set.') }
if (!PRODUCT_PRICE_ID?.startsWith('price_')) { console.warn('Stripe Product Price ID seems invalid or is not set.') }
const formatCurrency = (cents: number): string => {
  const amount = typeof cents === 'number' ? cents / 100 : 0
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount) } catch (error) { console.error('Intl.NumberFormat failed:', error); return `$${amount.toFixed(2)}` }
}
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : Promise.resolve(null)

// Domain restriction - checkout only available on approved domains
const APPROVED_DOMAINS = [
  'localhost',
  '127.0.0.1',
  'studio-app.dopa.ge',
  'train.dopa.ge'
]

export const isCheckoutEnabled = (): boolean => {
  // Check for URL override flag to force "Not Available" (for testing)
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('disableCheckout') === 'true') {
    return false
  }

  // Check if current domain is approved
  const hostname = window.location.hostname
  return APPROVED_DOMAINS.some(domain => hostname === domain)
}

interface DiscountCode {
  code: string
  discountPercentage: number
  expiryDate: string
}

// API validation function for promo codes
const validatePromoCode = async (promoCode: string): Promise<{ success: boolean, valid?: boolean, code?: string, discountPercentage?: number, expiryDate?: string, message?: string, error?: string }> => {
  try {
    const response = await fetch('https://buxj4tmda4.execute-api.us-east-2.amazonaws.com/default/stripeWebHook', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: 'VALIDATE_CODE',
        code: promoCode
      })
    })

    return await response.json()
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    return { success: false, error: errorMessage }
  }
}

// Simplified form - only quantity needed (student emails collected post-payment)
interface FormValues { quantity: number }
const CheckoutSchema = Yup.object().shape({
  quantity: Yup.number().min(1, 'Must purchase at least 1 item').integer('Quantity must be a whole number').required('Quantity is required')
})
// --- End Setup ---

// --- Main Checkout Component ---
const CheckoutForm = (): ReactElement | null => {
  const stripe = useStripe()
  const [formError, setFormError] = useState<string | null>(null)
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [discountCode, setDiscountCode] = useState<DiscountCode | null>(null)
  const [promoCodeInput, setPromoCodeInput] = useState('DGE-')
  const [isValidatingCode, setIsValidatingCode] = useState(false)
  const [codeValidationResult, setCodeValidationResult] = useState<{ success: boolean, valid?: boolean, discountPercentage?: number, expiryDate?: string, message?: string } | null>(null)

  // Handle discount code validation
  const validateDiscountCode = async (code: string): Promise<void> => {
    setIsValidatingCode(true)
    setCodeValidationResult(null)

    try {
      const result = await validatePromoCode(code)
      setCodeValidationResult(result)

      if (result.success && result.valid === true) {
        setDiscountCode({
          code: result.code ?? code,
          discountPercentage: result.discountPercentage ?? 0,
          expiryDate: result.expiryDate ?? ''
        })
      }
    } catch (_error) {
      setCodeValidationResult({ success: false, message: 'Failed to validate code' })
    } finally {
      setIsValidatingCode(false)
    }
  }

  // Normalize any user input (typing or paste) to a single DGE- prefix and 6-char code
  const normalizePromoCode = (raw: string): string => {
    if (typeof raw !== 'string') return 'DGE-'
    const upper = raw.toUpperCase()
    // Remove leading DGE (with/without hyphen), strip non-alphanumerics, cap at 6 chars
    const tail = upper.replace(/^DGE-?/, '').replace(/[^A-Z0-9]/g, '')
    return 'DGE-' + tail.slice(0, 6)
  }

  const handlePromoCodeChange = (value: string): void => {
    const normalized = normalizePromoCode(value)
    setPromoCodeInput(normalized)
    // Auto-validate when we have a complete code (DGE- plus 6 characters)
    if (/^DGE-[A-Z0-9]{6}$/.test(normalized)) {
      void validateDiscountCode(normalized)
    }
  }

  const handleDiscountModalClose = (): void => {
    setShowDiscountModal(false)
    setPromoCodeInput('DGE-')
    setCodeValidationResult(null)
  }

  const handleDiscountContinue = (): void => {
    setShowDiscountModal(false)
    setPromoCodeInput('DGE-')
    setCodeValidationResult(null)
  }

  const handleSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>): Promise<void> => {
    setFormError(null)
    if (stripe == null) {
      logger.error(new Error('Stripe.js not loaded'))
      setFormError('Payment system not ready.')
      actions.setSubmitting(false)
      return
    }

    actions.setSubmitting(true)
    try {
      // Deferred flow: no student data sent - will be collected post-payment
      const response = await axios.post('https://aote22c7zh.execute-api.us-east-2.amazonaws.com/default/stripeCheckout', {
        priceId: PRODUCT_PRICE_ID,
        quantity: values.quantity,
        flow: 'deferred', // NEW: indicates student data will be collected post-payment
        discountCode: discountCode?.code ?? null,
        discountPercentage: discountCode?.discountPercentage ?? 0,
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/checkout`
      })
      const sessionId = response?.data?.sessionId as string | undefined

      if (sessionId == null || sessionId === '') { throw new Error('Failed to create checkout session.') }
      const result = await stripe.redirectToCheckout({ sessionId })
      if (result?.error != null) { throw result.error }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }, message?: string }
      logger.error(`Checkout error: ${JSON.stringify(error)}`)
      const message = axiosError?.response?.data?.message ?? axiosError?.message ?? 'An unexpected error occurred.'
      setFormError(message)
      toast.error(message, { position: toast.POSITION.TOP_CENTER })
    } finally {
      actions.setSubmitting(false)
    }
  }

  const initialValues: FormValues = { quantity: 1 }
  if (stripe == null) { return <div className='text-center p-10'>Loading Payment Form...</div> }

  return (
    <Formik initialValues={initialValues} validationSchema={CheckoutSchema} onSubmit={handleSubmit} validateOnMount enableReinitialize={false}>
      {({ values, errors, touched, isSubmitting, isValid }: FormikProps<FormValues>) => {
        const baseTotalCents = PRODUCT_PRICE_CENTS * (values.quantity || 0)
        const discountAmount = discountCode != null ? (baseTotalCents * discountCode.discountPercentage / 100) : 0
        const totalCents = baseTotalCents - discountAmount

        return (
        // Responsive container - mobile: full width with padding, tablet: 90%, desktop: 700px max
          <div
            className='pt-2 sm:pt-5 px-4 sm:px-6 md:px-8 lg:px-0 stripe-checkout-container'
            style={{
              maxWidth: '700px',
              width: '100%',
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {/* Use expandable prop without modifying the Modal component itself */}
            <ModalStripe tall>
              <Form className='relative w-full min-h-[700px]'>
                {/* Top Section - Header + Content */}
                <div>
                  {/* Header Section */}
                  <div className='mb-2 sm:mb-4 md:mb-6' style={{ color: '#1A7A52' }}>
                    <Text className='font-bold text-center text-2xl sm:text-3xl md:text-4xl' bold size='LG' text='Checkout' type='paragraph' />
                  </div>

                  {/* Main Content Area */}
                  <div className='w-full space-y-2 sm:space-y-4 md:space-y-5'>
                  {/* Product Info */}
                  <div className='p-2 sm:p-3 rounded-md' style={{ backgroundColor: '#DBE3DF', border: '1px solid #2B2D2C', color: '#101111' }}>
                    <Text className='font-semibold text-base sm:text-lg inline' text={PRODUCT_NAME} />
                    <Text className='text-sm sm:text-base inline' text={` | ${formatCurrency(PRODUCT_PRICE_CENTS)} per student`} />
                  </div>

                  {/* Quantity Input */}
                  <div>
                    <label htmlFor='quantity' className='block text-xs sm:text-sm font-medium mb-0.5 sm:mb-1' style={{ color: '#101111' }}>How many students need access?</label>
                    <Field name='quantity'>
                      {({ field }: { field: { name: string, value: number, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, onBlur: (e: React.FocusEvent<HTMLInputElement>) => void } }) => (
                        <Input
                          id='quantity'
                          data-testid='input-quantity'
                          className='w-full py-2 sm:py-3 px-3 sm:px-4 text-base sm:text-lg leading-tight min-h-[44px]'
                          style={{ borderColor: '#101111', color: '#101111' }}
                          type='number' min='1' placeholder='Quantity' {...field}
                        />
                      )}
                    </Field>
                    {errors.quantity != null && touched.quantity === true
                      ? <div className='text-[#ff674c] pt-1 text-sm min-h-[20px]' aria-live='polite'>{errors.quantity}</div>
                      : <div className='min-h-[20px]' aria-hidden='true' />}
                  </div>

{/* Family purchase info */}
                    <div className='p-3 sm:p-4 rounded-md' style={{ backgroundColor: '#DBE3DF', border: '1px solid #2B2D2C', color: '#101111' }}>
                      <Text
                        className='text-xs sm:text-sm leading-snug'
                        size='SM'
                        text="Multiple teens or young adults in your family? Purchase 1 for each – they'll each receive their own login credentials."
                      />
                    </div>

                  {/* General Form Errors */}
                    {formError != null && (
                      <div className='text-[#ff674c] mt-4 p-3 bg-red-100 border border-red-400 rounded text-center text-sm' aria-live='polite'>
                        {formError}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Section - anchored to bottom */}
                <div className='absolute bottom-1 left-0 right-0 w-full px-4 sm:px-6'>
                  {/* Applied Discount Display */}
                  {discountCode != null && (
                    <div className='mb-1.5 sm:mb-2 text-center'>
                      <div className='text-xs sm:text-sm' style={{ color: '#1A7A52' }}>
                        Applied: {discountCode.code} (-{discountCode.discountPercentage}%)
                      </div>
                      <div className='text-xs sm:text-sm' style={{ color: '#101111' }}>
                        Discount: -{formatCurrency(discountAmount)}
                      </div>
                    </div>
                  )}

                  <div className='mb-2 sm:mb-3 text-center' style={{ color: '#101111' }}>
                    <Text className='text-lg sm:text-xl font-semibold' text={`Total: ${formatCurrency(totalCents)}`} />
                  </div>
                  <Button
                    id='button-proceed-to-payment'
                    data-testid='button-proceed-to-payment'
                    className='diamond-btn min-h-[48px] sm:h-[52px] w-full font-bold flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed'
                    style={{ backgroundColor: '#1A7A52', color: '#F2F6F4' }}
                    type='submit'
                    loading={isSubmitting}
                    disabled={isSubmitting || stripe == null || !isValid}
                  >
                    <Text className='text-base sm:text-lg md:text-xl' size='BASE' text='Continue to Payment →' type='span' />
                  </Button>

                  {/* Discount Code Link */}
                  <div className='mt-3 sm:mt-4 text-center'>
                    <button
                      type='button'
                      onClick={() => { setShowDiscountModal(true) }}
                      className='hover:text-white transition-colors text-xs sm:text-sm underline'
                      style={{ color: '#3E4140' }}
                    >
                      I have a discount code
                    </button>
                  </div>

                  {/* Powered by Stripe */}
                  <div className='mt-3 sm:mt-4 flex items-center justify-center'>
                    <img
                      src='/assets/images/powerdByStripe.svg'
                      alt='Powered by Stripe'
                      className='h-5 sm:h-6'
                      style={{ filter: 'brightness(0) saturate(100%) invert(26%) sepia(4%) saturate(698%) hue-rotate(135deg) brightness(92%) contrast(87%)' }}
                    />
                  </div>
                </div>

                {/* Discount Code Modal */}
                <PerceptionBarModal
                  isOpen={showDiscountModal}
                  onClose={handleDiscountModalClose}
                  className='p-4 sm:p-6 rounded-lg max-w-[calc(100%-2rem)] sm:max-w-md w-full mx-4 relative z-50'
                  overlayClassName='fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-40'
                >
                  <div style={{ backgroundColor: '#F2F6F4', padding: '1rem', borderRadius: '0.5rem', color: '#101111' }}>
                    <h2 className='text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-center'>Enter Discount Code</h2>

                    <div className='mb-3 sm:mb-4'>
                      <Input
                        type='text'
                        value={promoCodeInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handlePromoCodeChange(e.target.value) }}
                        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                          const pasted = e.clipboardData?.getData('text') ?? ''
                          const normalized = normalizePromoCode(pasted)
                          e.preventDefault()
                          setPromoCodeInput(normalized)
                          if (/^DGE-[A-Z0-9]{6}$/.test(normalized)) {
                            void validateDiscountCode(normalized)
                          }
                        }}
                        placeholder='DGE-1F7698'
                        className='w-full py-3 px-4 text-base sm:text-lg leading-tight min-h-[44px]'
                        style={{ borderColor: '#101111', color: '#101111' }}
                        maxLength={10}
                      />
                    </div>

                    {isValidatingCode && (
                      <div className='text-center mb-3 sm:mb-4' style={{ color: '#1A7A52' }}>
                        <Text className='text-xs sm:text-sm' text='Validating code...' />
                      </div>
                    )}

                    {codeValidationResult != null && (
                      <div className='mb-3 sm:mb-4'>
                        {codeValidationResult.success && codeValidationResult.valid === true
                          ? (
                            <div className='text-center'>
                              <div className='text-xs sm:text-sm mb-2' style={{ color: '#1A7A52' }}>
                                ✓ Valid code! {codeValidationResult.discountPercentage}% discount
                              </div>
                              <div className='text-xs mb-3 sm:mb-4' style={{ color: '#101111' }}>
                                Expires: {codeValidationResult.expiryDate != null ? new Date(codeValidationResult.expiryDate).toLocaleDateString() : 'N/A'}
                              </div>
                              <div className='text-center'>
                                <div className='text-xs sm:text-sm mb-2' style={{ color: '#101111' }}>
                                  Discount: -{formatCurrency((baseTotalCents * (codeValidationResult.discountPercentage ?? 0) / 100))}
                                </div>
                              </div>
                              <Button
                                onClick={handleDiscountContinue}
                                className='diamond-btn min-h-[44px] sm:h-[40px] w-full font-bold flex justify-center items-center'
                                style={{ backgroundColor: '#1A7A52', color: '#F2F6F4' }}
                              >
                                <Text className='text-base sm:text-lg' text='Continue' />
                              </Button>
                            </div>
                            )
                          : (
                            <div className='text-center'>
                              <div className='text-red-400 text-xs sm:text-sm mb-3 sm:mb-4'>
                                {codeValidationResult.message ?? 'Invalid discount code'}
                              </div>
                              <Button
                                onClick={handleDiscountModalClose}
                                className='diamond-btn min-h-[44px] sm:h-[40px] w-full font-bold flex justify-center items-center'
                                style={{ backgroundColor: '#1A7A52', color: '#F2F6F4' }}
                              >
                                <Text className='text-base sm:text-lg' text='Cancel' />
                              </Button>
                            </div>
                            )}
                      </div>
                    )}

                    {codeValidationResult == null && !isValidatingCode && (
                      <div className='text-center'>
                        <Button
                          onClick={handleDiscountModalClose}
                          className='diamond-btn min-h-[44px] sm:h-[40px] w-full font-bold flex justify-center items-center'
                          style={{ backgroundColor: '#1A7A52', color: '#F2F6F4' }}
                        >
                          <Text className='text-base sm:text-lg' text='Cancel' />
                        </Button>
                      </div>
                    )}
                  </div>
                </PerceptionBarModal>
              </Form>
            </ModalStripe>
          </div>
        )
      }}
    </Formik>
  )
}

// --- Main Page Component (wrapping with Elements provider) ---
function StripeCheckoutPage (): ReactElement {
  // Check if checkout is enabled on this domain
  const checkoutEnabled = isCheckoutEnabled()

  // Add custom styles to override Modal backgrounds and ensure mobile responsiveness
  // (hooks must be called unconditionally)
  React.useEffect(() => {
    if (!checkoutEnabled) return
    // Create a style element
    const styleEl = document.createElement('style')
    // Define CSS to override Modal backgrounds and fix mobile constraints
    styleEl.innerHTML = `
            /* Match outer container to page background */
            .stripe-checkout-container > div.bg-black {
                background-color: #101111 !important;
            }
            
            /* Only override the inner content area background */
            .stripe-checkout-container .diamond-corner > div.inner {
                background-color: #F2F6F4 !important;
            }
            
            /* Checkout-specific green glow effect (overrides global cyan glow) */
            .stripe-checkout-container .glow-effect {
                filter: drop-shadow(0 8px 32px rgba(26, 122, 82, 0.15)) drop-shadow(0 4px 16px rgba(26, 122, 82, 0.1)) !important;
            }
            
            /* Mobile responsive overrides */
            @media (max-width: 640px) {
                .stripe-checkout-container,
                .stripe-checkout-container > div,
                .stripe-checkout-container > div > div,
                .stripe-checkout-container > div > div > div {
                    max-width: 100% !important;
                    width: 100% !important;
                    min-width: 0 !important;
                }
            }
        `
    // Add the style element to the document head
    document.head.appendChild(styleEl)

    // Cleanup function to remove the style element when component unmounts
    return () => {
      document.head.removeChild(styleEl)
    }
  }, [checkoutEnabled])

  // Inject global styles to force scrolling
  React.useEffect(() => {
    if (!checkoutEnabled) return
    document.documentElement.style.overflow = 'auto'
    document.body.style.overflow = 'auto'
    document.body.style.height = 'auto'
    document.body.style.minHeight = '100vh'
    document.body.style.position = 'relative'

    // Cleanup
    return () => {
      document.documentElement.style.removeProperty('overflow')
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('height')
      document.body.style.removeProperty('min-height')
      document.body.style.removeProperty('position')
    }
  }, [checkoutEnabled])

  // Show "Not Available" message if checkout is disabled
  if (!checkoutEnabled) {
    return (
      <div style={{
        minHeight: '100vh',
        overflow: 'auto',
        position: 'relative',
        backgroundColor: '#101111',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      >
        <header className='absolute left-6 top-6 md:left-[40px] md:top-[40px]'>
          <img src={logo} className='w-[120px] md:w-[138px]' alt='logo' />
        </header>
        <div className='text-center p-8'>
          <h1 className='text-2xl font-bold text-white mb-4'>
            Checkout Not Available
          </h1>
          <p className='text-gray-400'>
            This page is not available on this domain.
          </p>
        </div>
      </div>
    )
  }

  if (!STRIPE_PUBLISHABLE_KEY?.startsWith('pk_') || stripePromise == null) {
    return (
      <div className='py-10 px-4 text-center text-red-500'>
        <h2 className='text-xl font-bold mb-2'>Configuration Error</h2>
        <p>Payment processing is currently unavailable. Please contact support.</p>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      overflow: 'auto',
      position: 'relative',
      backgroundColor: '#101111'
    }}
    >
      {/* Logo - centered on mobile, positioned top-left on desktop */}
      <header className='flex justify-center pt-4 pb-1 sm:absolute sm:left-6 sm:top-6 sm:pt-0 sm:pb-0 md:left-[40px] md:top-[40px]' aria-hidden='true'>
        <img src={logo} className='w-[80px] sm:w-[120px] md:w-[138px]' alt='logo' />
      </header>
      <Elements stripe={stripePromise}>
        <CheckoutForm />
      </Elements>
    </div>
  )
}

export default StripeCheckoutPage
